import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const TOKEN_KEY = "onedot6_auth_token";

const Ctx = createContext({
  user: null,
  token: null,
  loading: true,
  signIn: async () => { throw new Error("no auth"); },
  completeTwoFactor: async () => { throw new Error("no auth"); },
  signOut: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setLoading(false); return; }
    try {
      const u = await api.me(t);
      setUser(u);
      setToken(t);
    } catch (err) {
      console.debug("Session restore failed; clearing stored token", err);
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const signIn = async (email, password) => {
    const res = await api.login(email, password);
    if (res?.requires_2fa) {
      return { requires2FA: true, twoFactorToken: res.two_factor_token };
    }
    localStorage.setItem(TOKEN_KEY, res.token);
    setUser(res.user);
    setToken(res.token);
    return { user: res.user };
  };

  const completeTwoFactor = async (twoFactorToken, code) => {
    const res = await api.twoFactorLogin(twoFactorToken, code);
    localStorage.setItem(TOKEN_KEY, res.token);
    setUser(res.user);
    setToken(res.token);
    return res.user;
  };

  const signOut = async () => {
    if (token) { try { await api.logout(token); } catch (err) { console.debug("Logout request failed; clearing session locally", err); } }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setToken(null);
  };

  return (
    <Ctx.Provider value={{ user, token, loading, signIn, completeTwoFactor, signOut }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  return useContext(Ctx);
}
